import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import CheckIcon from '../../svgs/CheckIcon';
import CloseIcon from '../../svgs/CloseIcon';
import ExclamationIcon from '../../svgs/ExclamationIcon';
import InfoGlyphIcon from '../../svgs/InfoGlyphIcon';
import Spinner from './Spinner';

import type { Toast, ToastVariant } from '../../stores/toast';

/** Fill of the status badge, per variant. The badge is the only coloured part
 *  of the toast — the surface stays neutral so an error and a success read at
 *  the same visual volume. */
const BADGE_FILL: Record<ToastVariant, string> = {
  success: 'var(--primary)',
  error: 'var(--destructive)',
  warning: 'var(--accent)',
  info: 'var(--muted-foreground)',
  pending: 'transparent',
};

/** Glyph colour, chosen for contrast against the badge fill. */
const GLYPH_COLOR: Record<ToastVariant, string> = {
  success: 'var(--primary-foreground)',
  error: 'var(--destructive-foreground)',
  warning: 'var(--accent-foreground)',
  info: 'var(--background)',
  pending: 'var(--muted-foreground)',
};

const SLIDE_OUT_MS = 140;

const StatusGlyph = ({ variant }: { variant: ToastVariant }) => {
  const strokeColor = GLYPH_COLOR[variant];
  if (variant === 'pending') {
    // A spinner rather than a static glyph — the whole point of the pending
    // toast is to read as "still working", not as a result.
    return <PendingSpinner size="sm" />;
  }
  if (variant === 'success') {
    return <CheckIcon width="9" height="9" fillColor={strokeColor} />;
  }
  if (variant === 'info') {
    return <InfoGlyphIcon strokeColor={strokeColor} />;
  }
  return <ExclamationIcon strokeColor={strokeColor} />;
};

const ToastItem = observer(({ toast }: { toast: Toast }) => {
  const videoCreator = useVideoCreatorStore();
  const {
    id,
    state = 'success',
    message,
    persistent,
    action,
    secondaryAction,
    progress,
  } = toast;
  // A toast tracking a background job carries its progress; clamp it so a
  // caller that overshoots (or has not started) still renders a sane bar.
  const progressPercent =
    typeof progress === 'number' && Number.isFinite(progress)
      ? Math.round(Math.min(100, Math.max(0, progress)))
      : undefined;

  const [slidingOut, setSlidingOut] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hasFocus, setHasFocus] = useState(document.hasFocus());

  // Auto-dismiss budget is spent only while the toast is actually readable —
  // the window focused and the pointer away — so a backgrounded tab or a user
  // reading the message never burns the timer.
  const remainingRef = useRef(toast.delay ?? -1);
  const startedAtRef = useRef(Date.now());

  const dismiss = useCallback(() => setSlidingOut(true), []);

  const handleFocusChange = useCallback(
    () => setHasFocus(document.hasFocus()),
    [],
  );

  useEffect(() => {
    window.addEventListener('focus', handleFocusChange);
    window.addEventListener('blur', handleFocusChange);
    return () => {
      window.removeEventListener('focus', handleFocusChange);
      window.removeEventListener('blur', handleFocusChange);
    };
  }, [handleFocusChange]);

  const paused = !hasFocus || isHovered;

  // A toast replaced in place (`updateToast`, e.g. pending → success) keeps its
  // id, so this item never remounts — its budget has to be re-armed or the
  // outcome would inherit the pending toast's sticky -1. Keyed on the toast
  // object rather than its delay: `updateToast` builds a new object for the row
  // it replaces and reuses the reference for every other row, so identity means
  // "this row was replaced" even when the two delays resolve the same.
  useEffect(() => {
    remainingRef.current = toast.delay ?? -1;
    startedAtRef.current = Date.now();
  }, [toast]);

  useEffect(() => {
    if (persistent || remainingRef.current < 0 || slidingOut) return;

    if (paused) {
      remainingRef.current -= Date.now() - startedAtRef.current;
      return;
    }

    startedAtRef.current = Date.now();
    const timer = setTimeout(dismiss, Math.max(0, remainingRef.current));
    return () => clearTimeout(timer);
  }, [paused, persistent, slidingOut, dismiss, toast]);

  useEffect(() => {
    if (!slidingOut) return;
    const timer = setTimeout(() => videoCreator.dismissToast(id), SLIDE_OUT_MS);
    return () => clearTimeout(timer);
  }, [slidingOut, videoCreator, id]);

  return (
    <ToastRow
      slidingOut={slidingOut}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role={state === 'error' ? 'alert' : 'status'}
      data-testid="toast-notification"
      data-variant={state}
    >
      <Badge fill={BADGE_FILL[state]}>
        <StatusGlyph variant={state} />
      </Badge>
      <Body>
        <Message $clamp={progressPercent !== undefined}>{message}</Message>
        {progressPercent !== undefined && (
          <ProgressRow>
            <ProgressTrack
              role="progressbar"
              aria-valuenow={progressPercent}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <ProgressFill style={{ width: `${progressPercent}%` }} />
            </ProgressTrack>
            <ProgressLabel>{progressPercent}%</ProgressLabel>
          </ProgressRow>
        )}
      </Body>
      {action && (
        <ActionButton
          onClick={e => {
            e.stopPropagation();
            action.onClick();
            // Dropped from the queue synchronously rather than animated out:
            // an action often navigates, and an unmounted toast would never
            // run its exit timer — leaving it stuck in the queue.
            videoCreator.dismissToast(id);
          }}
        >
          {action.label}
        </ActionButton>
      )}
      {secondaryAction && (
        <ActionButton
          onClick={e => {
            e.stopPropagation();
            // A detour, not the outcome — the toast stays so the main action
            // is still there afterwards (see `ToastRequest.secondaryAction`).
            secondaryAction.onClick();
          }}
        >
          {secondaryAction.label}
        </ActionButton>
      )}
      <Close onClick={dismiss} aria-label="Dismiss notification">
        <CloseIcon width="12" height="12" strokeColor="currentColor" />
      </Close>
    </ToastRow>
  );
});

/**
 * The app's single notification surface: a stack of subtle toasts pinned to the
 * top-centre of the viewport, newest first. Mount once per route tree; every
 * transient success / error / warning / info message goes through
 * `videoCreator.showToast()` rather than a local banner.
 */
const ToastNotification = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const toasts = videoCreator.toasts;

  if (!toasts.length) return null;

  return (
    <Stack data-testid="toast-stack">
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </Stack>
  );
});

export default ToastNotification;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-8px);
  }
`;

const Stack = styled.div`
  position: fixed;
  top: var(--spacing-md);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm);
  z-index: 10000001;
  /* The gaps between toasts must not swallow clicks on the UI underneath. */
  pointer-events: none;
`;

const ToastRow = styled.div<{ slidingOut: boolean }>`
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  /* Wide enough for a message plus two actions on one line (PLAT-11877:
     'Translation to Dutch (Belgium, Flemish) is ready.' + Review Translation
     + Copy Review Link); a wrapped message reads as two toasts. */
  max-width: min(720px, calc(100vw - var(--spacing-xl)));
  padding: var(--spacing-sm) var(--spacing-sm) var(--spacing-sm)
    var(--spacing-md);
  background: var(--popover);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-card);
  box-shadow: var(--elevation-toast);
  color: var(--popover-foreground);
  font-size: var(--text-sm);
  line-height: var(--leading-text-sm);
  font-weight: var(--font-weight-normal);

  ${props =>
    props.slidingOut
      ? css`
          animation: ${slideOut} ${SLIDE_OUT_MS}ms ease-in forwards;
        `
      : css`
          animation: ${slideIn} 160ms ease-out;
        `}
`;

const Badge = styled.span<{ fill: string }>`
  flex: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${props => props.fill};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PendingSpinner = styled(Spinner)`
  width: 16px;
  height: 16px;
  color: var(--muted-foreground);
`;

const Body = styled.span`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2xs);
  min-width: 0;
`;

const Message = styled.span<{ $clamp: boolean }>`
  min-width: 0;

  /* With a progress bar underneath, the message is a label: keep it to one
     line so the bar, not a wrapping title, sets the toast's height. */
  ${props =>
    props.$clamp &&
    css`
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `}
`;

/* Slim track under the message — the pending spinner says "working", the bar
   says how far along. */
const ProgressRow = styled.span`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  min-width: 240px;
`;

const ProgressTrack = styled.span`
  flex: 1 1 auto;
  height: 4px;
  border-radius: 2px;
  background: var(--border-subtle);
  overflow: hidden;
`;

const ProgressFill = styled.span`
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--primary);
  transition: width 0.3s ease;
`;

const ProgressLabel = styled.span`
  flex: none;
  min-width: 3.5ch;
  text-align: right;
  font-size: var(--text-xs);
  color: var(--muted-foreground);
  font-variant-numeric: tabular-nums;
`;

const ActionButton = styled.button`
  flex: none;
  margin-left: var(--spacing-2xs);
  padding: 4px 12px;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: var(--radius-button);
  color: var(--foreground);
  font-family: inherit;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: var(--surface-elevated);
  }
`;

const Close = styled.button`
  flex: none;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--radius-input);
  color: var(--muted-foreground);
  cursor: pointer;
  transition:
    color 0.15s ease,
    background 0.15s ease;

  &:hover {
    color: var(--foreground);
    background: var(--surface-elevated);
  }
`;
